<template>
	<div class="d-flex flex-column flex h100">
		<navbar></navbar>
		<div class="app">
			<config-sidenav></config-sidenav>
			<div id="content" class="app-content box-shadow-3">
				<div class="navbar navbar-expand-lg">
					<!-- Page title -->
					<loading type="header" v-if="loading" />
					<div v-if="!loading" class="navbar-text nav-title flex" id="pageTitle">
						<i
							class="fas fa-fw fa-lock-alt"
							v-if="rubric.isScored"
							v-tippy="{ placement: 'bottom-start' }"
							title="This rubric has been used for scoring, so some options can no longer be changed"
						></i>
						{{ rubric.name }}
					</div>
				</div>

				<div class="content-main" style="background-color: white">
					<div class="h-100" v-if="!loading">
						<edit-pane :cancel="loadData" :save="saveRubric" :dirty="dirty" :valid="valid">
							<div class="padding">
								<!-- Page content goes here -->
								<div v-if="rubric" class="row m-0">
									<div class="col-md-7">
										<!-- Rubric Settings -->
										<div class="row">
											<form class="col-sm-7">
												<div class="form-group" :class="{ invalid: !valid.field('name') }">
													<label>Rubric Name</label>
													<input
														v-model="rubric.name"
														type="text"
														class="form-control"
														placeholder="Rubric Name ..."
													/>
													<!-- <small class="form-text text-muted">Name of the rubric</small> -->
												</div>
												<div
													class="form-group"
													:class="{
														invalid: !(valid.field('ref') && valid.field('ref_char')),
													}"
												>
													<label>Reference ID</label>
													<input
														v-model.trim="rubric.ref_id"
														type="text"
														class="form-control"
														placeholder="Reference ID ..."
														:disabled="rubric.isScored"
													/>
													<small class="form-text text-muted"
														>Reference ID must be unique within this tenant</small
													>
												</div>
												<div
													v-if="rubric.master_rubric && user.client.standards_rubric"
													class="form-group"
												>
													<label>Standard Rubric Scores</label>
													<div class="row">
														<div class="col-sm-6">
															<input
																v-model.number="rubric.standard_rubric_min"
																type="number"
																class="form-control"
																placeholder="Min ..."
															/>
														</div>
														<div class="col-sm-6">
															<input
																v-model.number="rubric.standard_rubric_max"
																type="number"
																class="form-control"
																placeholder="Max ..."
															/>
														</div>
													</div>

													<small class="form-text text-muted"
														>Min and Max of the Rubric generated from Standards</small
													>
												</div>
											</form>
											<div class="col-sm-4">
												<form role="form">
													<p>Settings</p>
													<div class="checkbox">
														<label class="md-check" :class="{ disabled: rubric.isScored }">
															<input v-model="rubric.collect_evidence" type="checkbox" />
															<i class="theme"></i>
															Collect Evidence
														</label>
													</div>
													<div class="checkbox">
														<label class="md-check" :class="{ disabled: rubric.isScored }">
															<input v-model="rubric.master_rubric" type="checkbox" />
															<i class="theme"></i>
															Flag as master
														</label>
													</div>
													<div class="checkbox">
														<label class="md-check" :class="{ disabled: rubric.isScored }">
															<input
																v-model="rubric.reverse_score_points"
																type="checkbox"
															/>
															<i class="theme"></i> Reverse Score Points
														</label>
													</div>
													<div class="checkbox">
														<label class="md-check" :class="{ disabled: rubric.isScored }">
															<input v-model="rubric.prefill_score" type="checkbox" />
															<i class="theme"></i> Prefill Scores
														</label>
													</div>
												</form>
											</div>
										</div>

										<!-- Numeric Traits -->
										<div class="row mb-3">
											<div class="col">
												<label>Numeric Traits</label>
												<table class="table mb-0">
													<thead>
														<tr>
															<th>Name</th>
															<th>Min</th>
															<th>Max</th>
															<th>Half Points</th>
															<th>Weight</th>
															<th></th>
														</tr>
													</thead>
													<tbody>
														<tr v-if="!rubric.traits.length">
															<td colspan="6" class="text-center">
																No Traits Configured
															</td>
														</tr>
														<tr v-for="trait in rubric.traits" :key="trait.id">
															<td class>
																<input
																	class="form-control"
																	v-model="trait.name"
																	type="text"
																/>
															</td>
															<td class="w100" v-if="!trait.separator">
																<input
																	class="form-control"
																	v-model.number="trait.min"
																	type="number"
																	:disabled="rubric.isScored"
																/>
															</td>
															<td class="w100" v-if="!trait.separator">
																<input
																	class="form-control"
																	v-model.number="trait.max"
																	type="number"
																	:disabled="rubric.isScored"
																/>
															</td>
															<td class="text-center" v-if="!trait.separator">
																<label
																	class="md-check"
																	:class="{ disabled: rubric.isScored }"
																>
																	<input
																		v-model="trait.use_half_points"
																		type="checkbox"
																	/>
																	<i class="theme"></i>
																</label>
															</td>
															<td class="w-80" v-if="!trait.separator">
																<input
																	class="form-control"
																	v-model="trait.weight"
																	type="number"
																	:disabled="rubric.isScored"
																/>
															</td>
															<td
																colspan="4"
																class="v-mid text-center"
																v-if="trait.separator"
															>
																<span class="text-muted">Section Title</span>
															</td>
															<td class="v-mid w90 text-right">
																<button
																	v-if="!trait.separator"
																	@click="editExemplars(trait)"
																	class="
																		btn btn-sm btn-icon btn-rounded
																		theme-accent
																		text-white
																		m-0
																	"
																	v-tippy
																	title="Exemplars"
																>
																	<i
																		class="fa fa-atlas"
																		style="font-size: 0.9rem"
																	></i>
																</button>
																<span
																	@click="editExemplars(trait)"
																	v-if="
																		!trait.separator &&
																		countTraitExemplars(trait) > 0
																	"
																	class="badge badge-pill up success clickable"
																	>{{ countTraitExemplars(trait) }}</span
																>
																<button
																	v-if="!trait.separator"
																	@click="editSPDs(trait)"
																	data-toggle="modal"
																	data-target="#spdEditModal"
																	class="
																		btn btn-sm btn-icon btn-rounded
																		theme-accent
																		text-white
																		m-0
																	"
																	v-tippy
																	title="Score Point Descriptors"
																>
																	<i
																		class="fa fa-comment-alt-lines"
																		style="font-size: 0.9rem"
																	></i>
																</button>
																<button
																	v-if="!trait.separator"
																	@click="editTrait(trait)"
																	data-toggle="modal"
																	data-target="#traitEditModal"
																	class="
																		btn btn-sm btn-icon btn-rounded
																		theme-accent
																		text-white
																		m-0
																	"
																	v-tippy
																	title="Trait Settings"
																>
																	<i class="fa fa-cog"></i>
																</button>
																<button
																	@click="removeTrait(trait)"
																	class="
																		btn btn-sm btn-icon btn-rounded
																		red
																		text-white
																		m-0
																	"
																	title="Remove Trait"
																	:disabled="rubric.isScored"
																>
																	<i class="fa fa-trash"></i>
																</button>
															</td>
														</tr>
													</tbody>
												</table>
												<div
													class="d-flex flex-row justify-content-start"
													style="padding: 0.6rem 0.75rem"
												>
													<button
														@click="addTrait()"
														class="btn btn-sm btn-success mr-3"
														:disabled="rubric.isScored"
													>
														Add Trait
													</button>
													<button
														@click="addSection()"
														class="btn btn-sm btn-success"
														:disabled="rubric.isScored"
													>
														Add Section
													</button>
												</div>
											</div>
										</div>

										<!-- Text Traits -->
										<div class="row">
											<div class="col">
												<label>Text Traits</label>
												<table class="table mb-0">
													<thead>
														<tr>
															<th>Name</th>
															<th>Type</th>
															<th>Required</th>
															<th></th>
														</tr>
													</thead>
													<tbody>
														<tr>
															<td
																class="text-center"
																v-if="!rubric.meta_traits.length"
																colspan="4"
															>
																No Text Traits Configured
															</td>
														</tr>

														<tr
															v-for="meta_trait in rubric.meta_traits"
															:key="meta_trait.id"
														>
															<td>
																<input
																	v-model="meta_trait.name"
																	type="text"
																	class="form-control"
																	:disabled="rubric.isScored"
																/>
															</td>
															<td>
																<select
																	class="form-control"
																	v-model="meta_trait.meta_trait_type"
																	:disabled="rubric.isScored"
																>
																	<option :value="1">Text</option>
																	<option :value="2">Long Text</option>
																	<option :value="3">Text Select</option>
																</select>
															</td>
															<td>
																<div class="checkbox">
																	<label
																		class="md-check"
																		:class="{ disabled: rubric.isScored }"
																	>
																		<input
																			v-model="meta_trait.required"
																			type="checkbox"
																		/>
																		<i class="theme"></i>
																	</label>
																</div>
															</td>
															<td>
																<button
																	v-if="meta_trait.meta_trait_type == 3"
																	@click="editMetaTrait(meta_trait)"
																	data-toggle="modal"
																	data-target="#metaTraitEditModal"
																	class="
																		btn btn-sm btn-icon btn-rounded
																		theme-accent
																		text-white
																		m-0
																	"
																	title="Text Select Settings"
																	:disabled="rubric.isScored"
																>
																	<i class="fa fa-cog"></i>
																</button>
																<button
																	@click="removeMetaTrait(meta_trait)"
																	class="
																		btn btn-sm btn-icon btn-rounded
																		red
																		text-white
																		m-0
																	"
																	title="Remove Text Trait"
																	:disabled="rubric.isScored"
																>
																	<i class="fa fa-trash"></i>
																</button>
															</td>
														</tr>
													</tbody>
												</table>
												<div
													class="d-flex flex-row justify-content-start"
													style="padding: 0.6rem 0.75rem"
												>
													<button
														@click="addMetaTrait()"
														class="btn btn-sm btn-success"
														:disabled="rubric.isScored"
													>
														Add Text Trait
													</button>
												</div>
											</div>
										</div>
									</div>

									<!-- The Rubric -->
									<div class="col-md-5">
										<div class="col-sm-6" :class="{ invalid: !valid.field('anyTraits') }">
											<label>Preview</label>
											<rubric
												:outer-rubric="rubric"
												:score.sync="score"
												:complete.sync="complete"
												@exemplar="pickExemplarFromRubric"
											></rubric>
										</div>
										<div class="col-12 mt-4">
											<score
												v-if="rubric && score && rubric.traits.length"
												:score="score"
												:rubric="rubric"
												:type="1"
											></score>
										</div>
										<!-- Uncomment to debug the scores generated -->
										<!-- <div class="row">
											Complete:{{complete}}
											<br><pre>{{score}}</pre>
										</div>-->
									</div>
								</div>
							</div>
						</edit-pane>
						<save-optional-modal
							:dirty="dirty"
							:valid="valid"
							:save="saveRubric"
							:next="saveOptNext"
							:cancel="saveOptCancel"
							objectText="Rubric"
							:objectName="rubric.name"
							actionText="leave the page"
							v-model="saveOptModal"
						/>
					</div>
				</div>
			</div>
		</div>

		<!-- Trait Edit Modal -->
		<div class="modal" id="traitEditModal" data-backdrop="static" data-keyboard="false">
			<div v-if="editingTrait" class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5>
							Trait Details -
							<strong class="text-theme">{{ editingTrait.name }}</strong>
						</h5>
					</div>
					<div class="modal-body">
						<div class="row">
							<div class="col-md-8">
								<h5>Condition Codes</h5>

								<div class="checkbox float-right">
									<label class="md-check" :class="{ disabled: rubric.isScored }">
										<input v-model="editingTrait.inline_condition_codes" type="checkbox" />
										<i class="theme"></i> Inline Condition Codes
									</label>
								</div>
								<table class="table">
									<thead>
										<tr>
											<th>Symbol</th>
											<th class="w100">Sequence</th>
											<th>Action</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										<tr v-if="!editingTrait.condition_codes.length">
											<td colspan="4" class="text-center">No Condition Codes</td>
										</tr>

										<tr v-for="code in editingTrait.condition_codes" :key="code.sequence">
											<td>
												<input
													:disabled="rubric.isScored"
													type="text"
													v-model="code.symbol"
													class="form-control"
												/>
											</td>
											<td>
												<input
													:disabled="rubric.isScored"
													type="number"
													number
													v-model="code.sequence"
													class="form-control"
												/>
											</td>
											<td>
												<select
													:disabled="rubric.isScored"
													class="form-control"
													v-model="code.action"
												>
													<option :value="1">Score</option>
													<option :value="2">Review</option>
												</select>
											</td>
											<td>
												<button
													:disabled="rubric.isScored"
													@click="removeConditionCode(code)"
													class="btn btn-sm btn-icon btn-rounded red text-white m-0"
													title="Remove Trait"
												>
													<i class="fa fa-trash"></i>
												</button>
											</td>
										</tr>

										<tr>
											<td colspan="3">
												<button
													:disabled="rubric.isScored"
													@click="
														editingTrait.condition_codes.push({
															symbol: 'A',
															sequence: editingTrait.condition_codes.length,
															action: 1,
														})
													"
													class="btn-block btn success"
												>
													Add Condition Code
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div class="col-md-4">
								<h5>Masked Score Points</h5>
								<config-trait
									:trait="editingTrait"
									:rubric="rubric"
									:selected.sync="editingTrait.masked_score_points"
									:single="false"
								></config-trait>
							</div>
						</div>
					</div>

					<div class="modal-footer">
						<button
							@click="
								editingTrait = null;
								spcodeselected = [];
							"
							class="btn btn-flat"
							data-dismiss="modal"
						>
							Done
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Text Trait Edit Modal -->
		<div class="modal" id="metaTraitEditModal" data-backdrop="static" data-keyboard="false">
			<div v-if="editingMetaTrait" class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5>
							Text Options -
							<strong class="text-theme">{{ editingMetaTrait.name }}</strong>
						</h5>
					</div>
					<div class="modal-body">
						<table class="table">
							<thead>
								<tr>
									<th>Option Text</th>
								</tr>
							</thead>
							<tbody>
								<tr v-if="!textOpts.length">
									No Options Configured
								</tr>

								<tr v-for="opt in textOpts" :key="opt">
									<td>
										<input type="text" v-model="opt.val" class="form-control" />
									</td>
								</tr>

								<tr>
									<td>
										<button
											@click="textOpts.push({ val: 'Option' })"
											class="btn btn-sm btn-block success"
										>
											Add Option
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div class="modal-footer">
						<button @click="finishEditingMetaTrait()" class="btn btn-flat" data-dismiss="modal">
							Done
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Score Point Desciptor Edit Modal -->
		<div class="modal" id="spdEditModal" data-backdrop="static" data-keyboard="false">
			<div v-if="editingSPDTrait" class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5>
							Score Point Descriptors -
							<strong>{{ editingSPDTrait.name }}</strong>
						</h5>
					</div>
					<div class="modal-body">
						<div class="row">
							<div class="col-12">
								<table class="table">
									<thead>
										<tr>
											<th style="width: 60px">Score Point</th>
											<th>Descriptor</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="sp in editingSPDTrait.score_point_descriptors"
											:key="`${sp.sequence}`"
										>
											<td class="v-mid text-center">
												<h3>{{ sp.val >= 0 ? sp.val : sp.code }}</h3>
											</td>
											<td>
												<RichText
													:text.sync="sp.desc"
													:initial="sp.init_desc"
													:options="{ height: 104 }"
													integrated
													:refresh="editingSPDTrait.id"
												></RichText>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<div class="modal-footer">
						<button class="btn btn-flat" data-dismiss="modal">Done</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Exemplar Edit Modal -->
		<div class="modal" id="exemplarEditModal" data-backdrop="static" data-keyboard="false">
			<div v-if="editingExemplarTrait" class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="mb-0 mr-4">{{ editingExemplarTrait.name }}</h5>
						<h5 class="mb-0 float-right text-muted _400">Exemplar Assignments</h5>
					</div>

					<!-- Exemplar assignment -->
					<div v-if="!showExemplarResponse" class="modal-body p-0 nav-active-bg white">
						<div class="card-header">
							<ul class="nav nav-tabs card-header-tabs px-2">
								<template v-for="(sp, i) in editingExemplarTrait.score_point_descriptors">
									<li class="nav-item" :key="i" @click="pickExemplarSP(sp)">
										<a :class="{ active: editingExemplarSP == sp }" class="nav-link">
											Score: {{ sp.val == -1 ? sp.code : sp.val }}
											<span
												v-if="countSPExemplars(editingExemplarTrait, sp) > 0"
												@click="editExemplars(trait)"
												class="badge badge-pill success clickable"
												>{{ countSPExemplars(editingExemplarTrait, sp) }}</span
											>
										</a>
									</li>
								</template>
							</ul>
						</div>

						<dual-table
							ref="exemplarTable"
							leftLabel="Exemplars"
							leftSearch="Search exemplars..."
							:leftItems="hasProvider"
							:leftFields="hasFields"
							:leftControl="hasControl"
							rightLabel="Exemplar Pool"
							rightSearch="Search proposed exemplars..."
							:rightItems="canProvider"
							:rightFields="canFields"
							:rightControl="canControl"
						>
							<!-- Left table slots -->
							<!-- Name Virtual Column -->
							<template slot="left:response" slot-scope="data">
								<div v-if="data.item.created_by">
									<strong>By:</strong>
									<span>{{ data.item.created_by.full_name }}</span>
								</div>
								<div v-if="!data.item.created_by">
									<strong>By:</strong>
									<span>System</span>
								</div>
								<div class="text-muted">{{ fs.medDate(data.item.created_at) }}</div>
							</template>

							<!-- Actions -->
							<template slot="left:actions" slot-scope="data">
								<nobr>
									<div class="mx-auto">
										<button
											class="btn btn-sm btn-icon btn-rounded theme-accent text-white m-0"
											v-tippy
											title="View"
											@click="viewExemplarResponse(data.item)"
										>
											<i class="far fa-search"></i>
										</button>
										<button
											v-if="!data.item.inProgress"
											class="btn btn-sm btn-icon btn-rounded red text-white m-0"
											@click="unassignExemplar(data.item)"
											v-tippy
											title="Remove from exemplars"
										>
											<i class="far fa-minus"></i>
										</button>
										<button
											v-if="data.item.inProgress"
											class="btn btn-sm btn-icon btn-rounded m-0"
											v-tippy
											title="Removing..."
										>
											<loading type="icon" />
										</button>
									</div>
								</nobr>
							</template>

							<!-- Right table slots -->
							<!-- Name Virtual Column -->
							<template slot="right:response" slot-scope="data">
								<div v-if="data.item.created_by">
									<strong>By:</strong>
									<span>{{ data.item.created_by.full_name }}</span>
								</div>
								<div v-if="!data.item.created_by">
									<strong>By:</strong>
									<span>System</span>
								</div>
								<div class="text-muted">{{ fs.medDate(data.item.created_at) }}</div>
							</template>

							<!-- Actions -->
							<template slot="right:actions" slot-scope="data">
								<nobr>
									<div class="mx-auto">
										<button
											class="btn btn-sm btn-icon btn-rounded theme-accent text-white m-0"
											v-tippy
											title="View"
											@click="viewPoolExemplarResponse(data.item)"
										>
											<i class="far fa-search"></i>
										</button>
										<button
											v-if="!data.item.inProgress"
											class="btn btn-sm btn-icon btn-rounded success text-white m-0"
											@click="assignExemplar(data.item)"
											v-tippy
											title="Add as exemplar"
										>
											<i class="far fa-plus"></i>
										</button>
										<button
											v-if="data.item.inProgress"
											class="btn btn-sm btn-icon btn-rounded m-0"
											v-tippy
											title="Adding..."
										>
											<loading type="icon" />
										</button>
									</div>
								</nobr>
							</template>
						</dual-table>
					</div>

					<!-- Exemplar response view -->
					<div v-if="showExemplarResponse" class="modal-body p-0 nav-active-bg white">
						<div class="navbar navbar-light bg-white b-b">
							<a class="flex navbar-brand">
								<div class="flex">
									<h5 class="mb-0">Score {{ editingExemplarSP.val }} Exemplar</h5>
								</div>
							</a>
							<a class="btn-nav-topright b-l ml-0" @click="unviewExemplarResponse">
								<div>
									<i class="fas fa-arrow-alt-left fa-fw" />
									Back
								</div>
							</a>
						</div>

						<exemplar :exemplar="showExemplarResponse" />
					</div>

					<div class="modal-footer">
						<button class="btn btn-flat" data-dismiss="modal">Done</button>
					</div>
				</div>
			</div>
		</div>

		<mzfooter></mzfooter>
	</div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.w60 {
	width: 80px;
}
.w90 {
	min-width: 148px;
}
.w100 {
	width: 90px;
}
.modal-lg {
	max-width: 1000px;
}
.card-header {
	background-color: white;
}
.badge.up {
	padding: 0.15rem 0.3rem;
	position: relative;
	top: -0.65rem;
	left: -1.1rem;
	margin-right: -1.45rem;
	font-size: 90%;
	border: 1px solid rgba(255, 255, 255, 0.75);
	background-clip: padding-box;
	min-width: 1rem;
}
.badge.badge-tab {
	padding: 0.15rem 0.3rem;
	position: relative;
	top: -0.7rem;
	left: -4.3rem;
	margin-right: -1.45rem;
	font-size: 90%;
	border: 1px solid rgba(255, 255, 255, 0.75);
	background-clip: padding-box;
	min-width: 1rem;
}
.clickable {
	cursor: pointer;
}
</style>

<script>
//UI Components

//Libraries
import _ from "lodash";
import moment from "moment";

//Services
import RubricService from "@/services/RubricService";
import ValidationService from "@/services/ValidationService";
import notie from "@/services/NotieService";
import fs from "@/services/FormatService";
import Utils from "@/services/Utils";
import $ from "jquery";

import ConfigTrait from "@/components/ConfigTrait";
import Rubric from "@/components/Rubric";
import Score from "@/components/Score";
import EditPane from "@/components/EditPane";
import SaveOptionalModal from "@/components/SaveOptionalModal";
import RichText from "@/components/RichText";
import DualTable from "@/components/DualTable";
import Exemplar from "@/components/Exemplar";
import Media from "@/components/viewer/Media";

//fields for b-table
let fields = [{ key: "name", sortable: true }];

export default {
	name: "RubricEdit",
	props: ["user"],
	data() {
		return {
			fs: fs,
			rubric: {},
			dirty: false,
			editingTrait: null,
			selected: [],
			spcodeselected: [],
			editingComments: [],
			editingMetaTrait: null,
			editingSPDTrait: null,
			editingExemplarTrait: null,
			editingExemplarSP: {
				exemplars: [],
			},
			textOpts: [],
			temp_rubric: null,
			score: null,
			complete: false,
			autoRef: false,
			loading: true,
			valid: {},
			saveOptModal: false,
			saveOptNext: () => {},
			saveOptCancel: () => {},

			//Exemplar assignment
			hasFields: [
				{
					key: "response",
					label: "Exemplars",
					tdClass: "p-custom pl-4 py-1",
				},
				{
					key: "actions",
					label: "",
					thStyle: { "text-align": "center", width: "80px" },
					tdClass: "p-custom py-0 v-mid pr-4",
				},
			],
			hasControl: {
				totalRows: 0,
				currentPage: 1,
				perPage: 10,
				filter: "",
				sortBy: "",
				sortDesc: false,
				emptyText: "No exemplars",
			},
			canFields: [
				{
					key: "response",
					label: "Proposed Exemplars",
					tdClass: "p-custom pl-4 py-1",
				},
				{
					key: "actions",
					label: "",
					thStyle: { "text-align": "center", width: "80px" },
					tdClass: "p-custom py-0 v-mid pr-4",
				},
			],
			canControl: {
				totalRows: 0,
				currentPage: 1,
				perPage: 10,
				filter: "",
				sortBy: "",
				sortDesc: false,
				emptyText: "No proposed exemplars",
			},
			showExemplarResponse: null,
		};
	},

	components: {
		ConfigTrait,
		Rubric,
		Score,
		EditPane,
		SaveOptionalModal,
		RichText,
		DualTable,
		Exemplar,
		Media,
	},

	created() {
		this.loadData();
		this.initValidation();
	},

	watch: {
		spcodeselected() {
			this.updateEditingCommentList();
		},
		"rubric.name"() {
			if (this.autoRef) {
				this.rubric.ref_id = fs.toGoodRefID(this.rubric.name);
			}
		},
		"rubric.ref_id"() {
			this.checkAutoRef();
		},
	},

	beforeRouteLeave(to, from, next) {
		if (this.dirty) {
			this.saveOptNext = () => {
				next();
			};
			this.saveOptCancel = () => {
				next(false);
			};
			this.saveOptModal = true;
		} else {
			next();
		}
	},

	methods: {
		initValidation() {
			this.valid = ValidationService.newValidator({
				name: {
					group: "details",
					errorMsg: "You must assign a name",
					func: () => {
						return this.rubric && this.rubric.name && this.rubric.name != "";
					},
				},
				ref: {
					group: "details",
					errorMsg: "You must assign a reference ID",
					func: () => {
						return this.rubric && this.rubric.ref_id && this.rubric.ref_id != "";
					},
				},
				ref_char: {
					group: "details",
					errorMsg:
						"Reference IDs may not contain spaces or any of the following characters: ! # % ^ * ( )  / [ ] { } < > ? | ' \" :",
					func: () => {
						return this.rubric && this.rubric.ref_id && fs.isGoodRefID(this.rubric.ref_id);
					},
				},
				anyTraits: {
					group: "rubric",
					errorMsg: "You must specify at least one trait",
					func: () => {
						return (
							this.rubric.master_rubric ||
							(this.rubric &&
								this.rubric.meta_traits &&
								this.rubric.traits &&
								(this.rubric.meta_traits.length > 0 || this.rubric.traits.length > 0))
						);
					},
				},
			});
		},

		blankRubric() {
			return {
				name: "",
				traits: [],
				meta_traits: [],
				score_point_descriptors: [],
			};
		},

		watchChanges() {
			if (this.unwatch) {
				this.unwatch();
			}
			this.unwatch = this.$watch(
				"rubric",
				(newc, old) => {
					// console.log(Utils.diff(newc, old));
					// console.log("marking dirty");
					this.dirty = true;
				},
				{ deep: true }
			);
		},
		setupTraits(traits) {
			for (var i = 0; i < traits.length; i++) {
				for (var j = 0; j < traits[i].score_point_descriptors.length; j++) {
					if (traits[i].score_point_descriptors[j].code != "") {
						traits[i].score_point_descriptors[j].val = traits[i].score_point_descriptors[j].code;
						if (!traits[i].score_point_descriptors[j].exemplars) {
							traits[i].score_point_descriptors[j].exemplars = [];
						}
					}
				}
			}
			return traits;
		},
		closeupTraits(traits) {
			for (var i = 0; i < traits.length; i++) {
				for (var j = 0; j < traits[i].score_point_descriptors.length; j++) {
					if (traits[i].score_point_descriptors[j].val == traits[i].score_point_descriptors[j].code) {
						traits[i].score_point_descriptors[j].val = -1;
					}
				}
			}
			return traits;
		},
		loadData() {
			this.dirty = false;

			if (this.$route.params.id == "new") {
				this.rubric = this.blankRubric();
				this.$set(this.rubric, "name", "New Rubric");
				this.$set(this.rubric, "ref_id", "new_rubric");
				this.loading = false;
				this.watchChanges();
			} else {
				let id = this.$route.params.id;
				return Promise.all([RubricService.fetchRubric(id), RubricService.isScored(id)])
					.then((resps) => {
						//resps[0] - fetchRubric
						this.rubric = resps[0].data;
						this.cleanupRubric();
						console.log("HELLO ", this.rubric.traits);
						this.setupTraits(this.rubric.traits);
						//resps[1] - isScored
						this.rubric.isScored = resps[1].data;
						this.markExistingComments();

						this.loading = false;
						this.checkAutoRef();
						this.watchChanges();
					})
					.catch((err) => {
						notie.error("Rubric not found, adding new rubric instead", err);
						this.$router.replace("/rubrics/new");
					});
			}
		},

		saveRubric() {
			this.cleanupRubric();
			delete this.rubric.proposed_exemplars;
			this.temp_rubric = this.rubric;
			this.temp_rubric.traits = this.closeupTraits(this.temp_rubric.traits);
			return RubricService.saveRubric(this.temp_rubric)
				.then((resp) => {
					notie.info("Rubric Saved");
					this.$router.replace("/rubrics/" + resp.data.id);
					this.loadData();
				})
				.catch((err) => {
					console.log(err);
					notie.error("Failed to save rubric", err);
				});
		},

		addTrait() {
			this.rubric.traits.push({
				name: "Trait " + (this.rubric.traits.length + 1),
				id: Utils.generateUUID(),
				sequence: this.rubric.traits.length + 1,
				min: 1,
				max: 5,
				weight: 1,
				condition_codes: [],
				score_point_codes: [],
				score_point_descriptors: [],
				masked_score_points: [],
			});
			this.cleanupRubric();
		},

		addSection() {
			this.rubric.traits.push({
				name: "Section " + (this.rubric.traits.length + 1),
				id: Utils.generateUUID(),
				sequence: this.rubric.traits.length + 1,
				min: 0,
				max: 0,
				weight: 1,
				condition_codes: [],
				score_point_codes: [],
				score_point_descriptors: [],
				masked_score_points: [],
				separator: true,
			});
			this.cleanupRubric();
		},

		removeTrait(trait) {
			this.rubric.traits = _.without(this.rubric.traits, trait);
			this.cleanupRubric();
		},

		removeMetaTrait(meta_trait) {
			this.rubric.meta_traits = _.without(this.rubric.meta_traits, meta_trait);
			this.cleanupRubric();
		},

		editTrait(trait) {
			this.editingTrait = trait;
		},

		addMetaTrait() {
			this.rubric.meta_traits.push({
				id: Utils.generateUUID(),
				meta_trait_type: 1,
				sequence: this.rubric.meta_traits.length + 1,
				name: "New Trait",
				required: false,
				options: "",
			});
			this.cleanupRubric();
		},

		editMetaTrait(meta_trait) {
			this.editingMetaTrait = meta_trait;
			this.textOpts = meta_trait.options.split("|").map((v) => {
				return { val: v };
			});
		},

		finishEditingMetaTrait() {
			this.editingMetaTrait.options = this.textOpts
				.map((v) => {
					return v.val;
				})
				.join("|");
			this.textOpts = [];
			this.editingMetaTrait = null;
		},

		removeConditionCode(code) {
			this.editingTrait.condition_codes = _.without(this.editingTrait.condition_codes, code);
		},

		removeScorePointCommentCode(code) {
			this.editingTrait.score_point_codes = _.without(this.editingTrait.score_point_codes, code);
			this.updateEditingCommentList();
		},

		updateEditingCommentList() {
			var selected = this.spcodeselected[0];
			if (this.editingTrait) {
				this.editingComments = _.filter(this.editingTrait.score_point_codes, {
					score_point: selected.mask_value,
				});
			}
		},

		addCommentCode() {
			var sp = this.spcodeselected[0].mask_value;

			this.editingTrait.score_point_codes.push({
				score_point: sp,
				code: this.nextCommentCodeLetter(this.editingTrait),
				text: "",
			});
			this.updateEditingCommentList();
		},

		intToCode(num) {
			let codes = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
			let code = "";
			while (num != -1) {
				let i = num % 26;
				code = codes[i] + code;
				num = Math.floor(num / 26) - 1;
			}
			return code;
		},

		nextCommentCodeLetter(trait) {
			let existingCodes = {};
			_.each(trait.score_point_codes, (spc) => {
				existingCodes[spc.code] = true;
			});

			let i = 0;
			let newCode = this.intToCode(i);
			while (existingCodes[newCode]) {
				i++;
				newCode = this.intToCode(i);
			}

			return newCode;
		},

		cleanupRubric() {
			var seq = 0;
			_.each(this.rubric.traits, (trait) => {
				trait.min = trait.min * 1;
				trait.max = trait.max * 1;
				trait.weight = trait.weight * 1;
				trait.sequence = seq;
				seq++;
			});

			seq = 0;
			_.each(this.rubric.meta_traits, (meta_trait) => {
				meta_trait.sequence = seq;
				seq++;
			});
			this.rubric.traits = _.sortBy(this.rubric.traits, "sequence");
		},

		editSPDs(trait) {
			this.setupDescriptors(trait);
			this.editingSPDTrait = trait;
		},

		editExemplars(trait) {
			this.setupDescriptors(trait);
			this.editingExemplarTrait = trait;
			this.editingExemplarSP = trait.score_point_descriptors[0];
			this.refreshTable();
		},

		setupDescriptors(trait) {
			var seq = 0;

			var step = 1;
			if (trait.use_half_points) {
				step = 0.5;
			}
			if (!trait.score_point_descriptors) {
				this.$set(trait, "score_point_descriptors", []);
			}
			var min = trait.min;
			var max = trait.max;

			var skip = trait.masked_score_points.map(function (mask) {
				return mask.mask_value;
			});

			if (this.rubric.reverse_score_points) {
				for (var i = max; i >= min; i -= step) {
					if (_.includes(skip, i)) {
						continue;
					}
					let existing = _.find(trait.score_point_descriptors, {
						val: i,
					});
					if (existing) {
						existing.sequence = seq;
						existing.init_desc = existing.desc;
						existing.keep = true;
					} else {
						trait.score_point_descriptors.push({
							val: i,
							desc: "",
							init_desc: "",
							sequence: seq,
							keep: true,
						});
					}
					seq++;
				}
			} else {
				for (var i = min; i <= max; i += step) {
					if (_.includes(skip, i)) {
						continue;
					}
					let existing = _.find(trait.score_point_descriptors, {
						val: i,
					});
					if (existing) {
						existing.sequence = seq;
						existing.init_desc = existing.desc;
						existing.keep = true;
					} else {
						trait.score_point_descriptors.push({
							val: i,
							desc: "",
							init_desc: "",
							sequence: seq,
							keep: true,
						});
					}
					seq++;
				}
			}

			trait.score_point_descriptors = _.sortBy(trait.score_point_descriptors, "sequence");

			_.each(trait.condition_codes, (cc) => {
				let existing = _.find(trait.score_point_descriptors, {
					code: cc.symbol,
				});
				if (existing) {
					existing.sequence = seq;
					existing.init_desc = existing.desc;
					existing.keep = true;
				} else {
					trait.score_point_descriptors.push({
						val: -1,
						code: cc.symbol,
						desc: "",
						init_desc: "",
						sequence: seq,
						keep: true,
					});
				}
				seq++;
			});

			trait.score_point_descriptors = _.filter(trait.score_point_descriptors, { keep: true });
			_.each(trait.score_point_descriptors, (spd) => {
				delete spd.keep;
			});
		},

		checkAutoRef() {
			let genRef = fs.toGoodRefID(this.rubric.name);
			this.autoRef = this.rubric.ref_id == genRef && !this.rubric.isScored;
		},

		markExistingComments() {
			_.each(this.rubric.traits, (trait) => {
				_.each(trait.score_point_codes, (spc) => {
					spc.locked = true;
				});
			});
		},

		//Exemplar assignment
		hasProvider(ctx) {
			let has = this.editingExemplarSP.exemplars;
			if (!has) {
				return new Promise(function (resolve, reject) {
					resolve([]);
				});
			}

			let index = this.hasControl.currentPage - 1;
			let perPage = this.hasControl.perPage;
			let start = index * perPage;
			let end = (index + 1) * perPage;
			if (end > has.length) {
				end = has.length;
			}

			return new Promise(function (resolve, reject) {
				resolve(has.slice(start, end));
			});
		},

		canProvider(ctx) {
			ctx.exclude = _.map(this.editingExemplarSP.exemplars, "response_id");
			let score = this.editingExemplarSP.val;
			let code = " ";
			if (this.editingExemplarSP.val == this.editingExemplarSP.code) {
				score = -1;
			}
			return RubricService.getExemplarPool(this.rubric.id, this.editingExemplarTrait.id, score, ctx)
				.then((resp) => {
					this.editingExemplarSP.code = this.editingExemplarSP.code || "";
					console.log("GOOD WE GOT RESULTS", resp.data.exemplars);
					this.canControl.totalRows = resp.data.totalRows;
					console.log(
						"test this.editingExemplarSP.code",
						this.editingExemplarSP.code,
						"vs exemplars[0].condition",
						resp.data.exemplars[0].condition,
						this.editingExemplarSP.code == resp.data.exemplars[0].condition
					);
					if (this.editingExemplarSP.code == resp.data.exemplars[0].condition) {
						return resp.data.exemplars;
					}
				})
				.catch((err) => {
					console.log(err);
					this.canControl.totalRows = 0;
					return [];
				});
		},

		assignExemplar(exemplar) {
			if (!this.editingExemplarSP.exemplars) {
				this.editingExemplarSP.exemplars = [];
			}
			console.log("assigning this exemplar ", exemplar);
			this.editingExemplarSP.exemplars.push({
				created_by_id: this.user.id,
				created_by: this.user,
				created_at: moment(),
				response_id: exemplar.response_id,
				exemplar_id: exemplar.id,
				id: this.uuidv4(),
			});
			this.rubric.proposed_exemplars = _.pull(this.rubric.proposed_exemplars, exemplar);
			this.$forceUpdate();
			this.refreshTable();
		},

		unassignExemplar(exemplar) {
			console.log("UNASSIGN", exemplar);
			this.editingExemplarSP.exemplars = _.pull(this.editingExemplarSP.exemplars, exemplar);
			this.rubric.proposed_exemplars.push(exemplar);
			this.$forceUpdate();
			this.refreshTable();
		},

		editExemplars(trait) {
			this.setupDescriptors(trait);
			this.editingExemplarTrait = trait;

			this.editingExemplarSP = trait.score_point_descriptors[0];
			console.log("this.editingExemplarTrait ", this.editingExemplarSP);
			this.refreshTable();
			$("#exemplarEditModal").modal("toggle");
		},

		pickExemplarSP(sp) {
			console.log("Editing this sp ", sp.exemplars);
			this.editingExemplarSP = sp;
			this.refreshTable();
		},

		viewExemplarResponse(exemplar) {
			console.log("viewExemplarResponse", exemplar);
			if (exemplar.response_id) {
				return RubricService.getPoolExemplar(exemplar.exemplar_id)
					.then((resp) => {
						exemplar.response = resp.data;
						this.showExemplarResponse = exemplar;
					})
					.catch((err) => {
						notie.error("Failed to view exemplar response", err);
					});
			} else {
				this.showExemplarResponse = exemplar;
			}
		},

		viewPoolExemplarResponse(exemplar) {
			console.log("viewPoolExemplarResponse", exemplar);
			if (exemplar.response_id) {
				return RubricService.getPoolExemplar(exemplar.id)
					.then((resp) => {
						exemplar.response = resp.data;
						this.showExemplarResponse = exemplar;
					})
					.catch((err) => {
						notie.error("Failed to view exemplar response", err);
					});
			} else {
				this.showExemplarResponse = exemplar;
			}
		},

		unviewExemplarResponse() {
			this.showExemplarResponse = null;
		},

		pickExemplarFromRubric(e) {
			_.each(this.rubric.traits, (trait) => {
				if (e.trait.id == trait.id) {
					_.each(trait.score_point_descriptors, (spd) => {
						if (e.sp.val == spd.val) {
							this.editExemplars(trait);
							this.pickExemplarSP(spd);
						}
					});
				}
			});
		},

		refreshTable() {
			if (!this.$refs.exemplarTable) {
				return;
			}
			this.$refs.exemplarTable.$children[0].refresh();
			this.$refs.exemplarTable.$children[2].refresh();
		},

		countTraitExemplars(trait) {
			let count = 0;
			_.each(this.rubric.proposed_exemplars, (prop) => {
				if (prop.exemplary_trait == trait.id) {
					count++;
				}
			});

			return count;
		},

		countSPExemplars(trait, sp) {
			let count = 0;
			_.each(this.rubric.proposed_exemplars, (prop) => {
				let exemplary = prop.exemplary_trait == trait.id;

				let score = _.find(prop.score.trait_scores, {
					trait_id: trait.id,
				});
				// console.log("search", prop.score.trait_scores, "for", trait.id);
				// console.log("found", exemplary, score, score.score, sp, sp.val, score.score == sp.val);
				if (exemplary && score.score != -1 && score.score == sp.val) {
					count++;
				}
				if (exemplary && score.score == -1 && score.condition == sp.code) {
					count++;
				}
			});

			return count;
		},

		uuidv4() {
			return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
				(c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
			);
		},
	},
};
</script>



// WEBPACK FOOTER //
// src/vues/SetupConfig/RubricEdit.vue